import React, { useState, useRef, useEffect } from 'react';
import { useSelectedClient } from '../context/SelectedClientContext';
import '../css/ClientSelector.css';

const ClientSelector = ({ clients, clientSearchQuery, setClientSearchQuery }) => {
    const { selectedClient, setSelectedClient } = useSelectedClient();
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef(null);

    // Filter clients based on search query
    const filteredClients = clients.filter(client =>
        client.name.toLowerCase().includes(clientSearchQuery.toLowerCase())
    );

    // Get selected client name for display
    const selectedClientData = clients.find(c => c.id === selectedClient);
    const selectedClientName = selectedClientData?.name || 'Select Client';

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Close on escape key
    useEffect(() => {
        const handleEscape = (event) => {
            if (event.key === 'Escape') setIsOpen(false);
        };

        document.addEventListener('keydown', handleEscape);
        return () => document.removeEventListener('keydown', handleEscape);
    }, []);

    return (
        <div className="client-selector" ref={dropdownRef}>
            {/* Trigger button */}
            <button
                className={`client-selector-trigger ${isOpen ? 'active' : ''} ${selectedClient ? 'has-selection' : ''}`}
                onClick={() => setIsOpen(!isOpen)}
                aria-expanded={isOpen}
                aria-haspopup="listbox"
            >
                <span className="trigger-icon">🏢</span>
                <span className="trigger-text">
                    <span className="trigger-label">Client</span>
                    <span className="trigger-value">{selectedClientName}</span>
                </span>
                <span className={`trigger-arrow ${isOpen ? 'open' : ''}`}>
                    <svg width="12" height="12" viewBox="0 0 12 12" fill="currentColor">
                        <path d="M2.5 4.5L6 8L9.5 4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
                    </svg>
                </span>
            </button>

            {/* Dropdown panel */}
            {isOpen && (
                <div className="client-dropdown">
                    <div className="client-dropdown-header">
                        <input
                            type="text"
                            placeholder="Search clients..."
                            value={clientSearchQuery}
                            onChange={(e) => setClientSearchQuery(e.target.value)}
                            className="client-search-input"
                            autoFocus
                        />
                    </div>
                    <div className="client-dropdown-list" role="listbox">
                        {filteredClients.length > 0 ? (
                            filteredClients.map(client => (
                                <button
                                    key={client.id}
                                    className={`client-dropdown-item ${selectedClient === client.id ? 'selected' : ''}`}
                                    onClick={() => {
                                        setSelectedClient(client.id);
                                        setIsOpen(false);
                                        setClientSearchQuery('');
                                    }}
                                    role="option"
                                    aria-selected={selectedClient === client.id}
                                >
                                    <span className="item-icon">
                                        {selectedClient === client.id ? '✓' : ''}
                                    </span>
                                    <span className="item-name">{client.name}</span>
                                </button>
                            ))
                        ) : (
                            <div className="client-dropdown-empty">
                                No clients found
                            </div>
                        )}
                    </div>
                    {selectedClient && (
                        <div className="client-dropdown-footer">
                            <button
                                className="clear-selection-btn"
                                onClick={() => {
                                    setSelectedClient(null);
                                    setIsOpen(false);
                                }}
                            >
                                Clear selection
                            </button>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default ClientSelector;
